import { useState, useEffect } from 'react';
import { socialAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

interface Comment {
  id: string;
  content: string;
  created_at: string;
  author: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_picture?: string;
    user_type: string;
  };
}

interface CommentSectionProps {
  postId: string;
  isOpen: boolean;
  onClose: () => void;
  initialCommentsCount: number;
  onCommentAdded?: () => void;
  onNavigateToProfile?: (userId: string) => void; // Add this prop
}

export default function CommentSection({ 
  postId, 
  isOpen, 
  onClose, 
  initialCommentsCount, 
  onCommentAdded,
  onNavigateToProfile // Add this prop
}: CommentSectionProps) {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && comments.length === 0) {
      fetchComments();
    }
  }, [isOpen, postId]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      // Using getPostComments as TypeScript suggests
      const response = await (socialAPI as any).getComments(postId);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add profile click handler
  const handleProfileClick = (userId: string) => {
    if (onNavigateToProfile) {
      onNavigateToProfile(userId);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() || submitting) return;

    setSubmitting(true);
    try {
      // Using the content string directly as per your API
      const response = await socialAPI.addComment(postId, newComment.trim());
      
      // Add the new comment to the list
      const newCommentObj: Comment = {
        id: response.data.comment_id || Date.now().toString(),
        content: newComment.trim(),
        created_at: new Date().toISOString(),
        author: {
          id: user!.id,
          username: user!.username,
          first_name: user!.first_name,
          last_name: user!.last_name || '',
          profile_picture: user!.profile_picture,
          user_type: user!.role
        }
      };
      
      setComments([...comments, newCommentObj]);
      setNewComment('');
      
      // Call the callback to update the comment count in the parent
      onCommentAdded?.();
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'now';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return `${Math.floor(diffInMinutes / 1440)}d`;
  };

  if (!isOpen) return null;

  return (
    <div className="border-t border-white/20 bg-white/40">
      {/* Comments Header */}
      <div className="px-6 py-4 border-b border-white/20 flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">
          Comments ({comments.length})
        </h3>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Comments List */}
      <div className="px-6 py-4 max-h-96 overflow-y-auto">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse flex space-x-3">
                <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-3 bg-gray-300 rounded w-1/4 mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : comments.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No comments yet. Be the first to comment!
          </p>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="flex space-x-3">
                {/* Make profile picture clickable */}
                <div 
                  className="w-8 h-8 bg-gray-300 rounded-full overflow-hidden flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-green-400 transition-all"
                  onClick={() => handleProfileClick(comment.author.id)}
                >
                  {comment.author.profile_picture ? (
                    <img 
                      src={comment.author.profile_picture} 
                      alt={comment.author.username}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-green-300 to-green-500 flex items-center justify-center text-white text-xs font-bold">
                      {comment.author.first_name[0]}{comment.author.last_name[0]}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    {/* Make name clickable */}
                    <span 
                      className="font-semibold text-sm text-gray-800 cursor-pointer hover:text-green-600 transition-colors"
                      onClick={() => handleProfileClick(comment.author.id)}
                    >
                      {comment.author.first_name} {comment.author.last_name}
                    </span>
                    {comment.author.user_type === 'trainer' && (
                      <span className="bg-green-400 text-gray-800 text-xs px-2 py-0.5 rounded-full">
                        Trainer
                      </span>
                    )}
                    <span className="text-gray-500 text-xs">
                      {formatTimeAgo(comment.created_at)}
                    </span>
                  </div>
                  <p className="text-gray-700 text-sm break-words">{comment.content}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Comment Section */}
      <div className="px-6 py-4 border-t border-white/20">
        <div className="flex space-x-3">
          <div className="w-8 h-8 bg-gray-300 rounded-full overflow-hidden flex-shrink-0">
            {user?.profile_picture ? (
              <img 
                src={user.profile_picture} 
                alt={user.username}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-green-300 to-green-500 flex items-center justify-center text-white text-xs font-bold">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
            )}
          </div>
          <div className="flex-1">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Write a comment..."
              className="w-full p-3 border border-gray-200 rounded-lg bg-white/80 resize-none focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
              rows={2}
              disabled={submitting}
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={handleSubmitComment}
                disabled={!newComment.trim() || submitting}
                className="bg-green-400 text-gray-800 px-4 py-2 rounded-full hover:bg-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {submitting ? 'Posting...' : 'Comment'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}